export type PageResourceType =
  'article' | 'detailpage' | 'frontpage' | 'funnel' | 'homepage';

/**
 * Every key is always present; values that do not apply are a real `null`
 * (never `undefined`, never omitted) to prevent data bleeding between pages.
 */
export type PageMetaEvent = {
  event: 'page_meta';
  /** Parent folders without leading or trailing slashes; `root` on the homepage. */
  est_resource_section: string;
  /** Last URL segment; `home` on the homepage. */
  est_resource_id: string;
  est_resource_type: PageResourceType;
  est_customer_type: CustomerType | null;
  est_funnel_type: string | null;
  est_funnel_channel: string | null;
};

export type PageMetaInput = {
  pathname: string;
  /** `null` on pages that are not content resources, which emit no event. */
  resourceType: PageResourceType | null;
  /** Only article templates maintain one. */
  customerGroup?: string;
  /** Whether other pages live below this one, as reported by the CMS export. */
  hasChildren?: boolean;
};

type CustomerType = 'gewerblich' | 'privat';

const CUSTOMER_TYPE_BY_GROUP: Record<string, CustomerType> = {
  businesscustomer: 'gewerblich',
  privatecustomer: 'privat',
};

const CUSTOMER_TYPE_BY_SECTION: Record<string, CustomerType> = {
  geschaeftskunden: 'gewerblich',
  privatkunden: 'privat',
};

/**
 * A product category lists its products, so the analytics blueprint counts it as
 * a hub even where it shares a template with ordinary content pages. Pages that
 * merely sit among the categories without listing anything stay detail pages.
 */
const isProductCategory = (segments: string[], hasChildren?: boolean) =>
  hasChildren === true &&
  segments.length === 3 &&
  segments[0].endsWith('kunden') &&
  segments[1] === 'produkte';

function deriveResourceType(
  segments: string[],
  declared: PageResourceType,
  hasChildren?: boolean
): PageResourceType {
  if (segments.length === 0) return 'homepage';
  if (isProductCategory(segments, hasChildren)) return 'frontpage';

  return declared;
}

function deriveCustomerType(segments: string[], customerGroup?: string) {
  return (
    CUSTOMER_TYPE_BY_GROUP[customerGroup ?? ''] ??
    CUSTOMER_TYPE_BY_SECTION[segments[0] ?? ''] ??
    null
  );
}

export default function buildPageMetaEvent({
  pathname,
  resourceType,
  customerGroup,
  hasChildren,
}: PageMetaInput): PageMetaEvent | null {
  if (!resourceType) return null;

  // `pathname` comes from the router, so it still carries any query and hash.
  const path = pathname.split(/[?#]/)[0];
  const segments = path
    .split('/')
    .filter(Boolean)
    .map((segment) => segment.toLowerCase());

  return {
    event: 'page_meta',
    est_resource_section: segments.slice(0, -1).join('/') || 'root',
    est_resource_id: segments.at(-1) ?? 'home',
    est_resource_type: deriveResourceType(segments, resourceType, hasChildren),
    est_customer_type: deriveCustomerType(segments, customerGroup),
    est_funnel_type: null,
    est_funnel_channel: null,
  };
}
